import React from 'react';
import { observer } from 'mobx-react';
import { CustomTabStore, MembershipTypeStore, SiteStore } from '@seedlang/stores';
import { computed } from 'mobx';
import autobind from 'autobind-decorator';
import InPlaceText from 'components/form/in_place_text';
import InPlaceCheckbox from 'components/form/in_place_checkbox';
import Cookies from 'js-cookie';
import { isFunction , compact, xor } from 'lodash';
import InPlaceImageUpload from 'components/form/in_place_image_upload';
import Text from 'components/text';
import IconInPlaceSelect from 'pages/icon_in_place_select';
import isBlank from 'is-blank';
import { isPresent } from '@seedlang/utils';
import DeleteButton from 'components/button/delete_button';
import Button from 'components/button/button';
import InfoTooltip from 'components/info_tooltip';
import Alert from 'components/alert';
import BackEndExplainer from '../back_end_explainer';
import { Link } from 'react-router';
import { AppUI } from '@seedlang/state';
import Spinner from 'components/spinner';
import Stack from 'components/layout/stack';
import Grid from 'components/layout/grid';
import GrayButton from 'components/button/gray_button';

@observer
class CustomTabEdit extends React.Component {
  static defaultProps = {
    whiteLabeled: false,
  };

  componentDidMount() {
    if (!this.props.whiteLabeled) {
      SiteStore.getIndex();
    }
    MembershipTypeStore.getIndex();
    if (isBlank(this.props.customTab)) {
      this.getCustomTab();
    }
  }

  componentDidUpdate(prevProps) {
    if (isPresent(this.props.routeParams) && isPresent(prevProps.routeParams) && this.props.routeParams.customTabId !== prevProps.routeParams.customTabId) {
      this.getCustomTab();
    }
  }

  componentWillUnmount() {
    if (CustomTabStore.hasShowData) {
      CustomTabStore.clearShowData();
    }
  }

  @computed get customTab() {
    return this.props.customTab || CustomTabStore.showData;
  }

  @computed get filteredMembershipTypes() {
    return MembershipTypeStore.indexData.filter(item => item.siteId === Cookies.get('site_id'));
  }

  @autobind afterUpdate() {
    if (isFunction(this.props.afterUpdate)) {
      this.props.afterUpdate();
    } else {
      this.getCustomTab();
    }
  }

  @autobind getCustomTab() {
    CustomTabStore.getShow({ ids: { customTabId: this.props.routeParams.customTabId }});
  }

  @autobind customTabOrWorksheetCustomTab(attribute) {
    return isPresent(this.props.worksheetCustomTab) ? this.props.worksheetCustomTab[attribute] : this.customTab[attribute];
  }

  @computed get model() {
    return isPresent(this.props.worksheetCustomTab) ? 'worksheet_custom_tabs' : 'custom_tabs';
  }

  @autobind onUpdateMembershipType(membershipTypeId) {
    let membershipTypeIds = compact(this.customTab.membershipTypeIds?.split(','));
    membershipTypeIds = xor(membershipTypeIds, [membershipTypeId]).join(',');
    CustomTabStore.update({ids: {customTabId: this.customTab.id}, data: {membershipTypeIds}}, this.afterUpdate);
  }

  @autobind onDelete() {
    CustomTabStore.destroy({ ids: { customTabId: this.customTab.id } }, this.afterUpdate);
  }

  render() {
    if (isPresent(this.customTab)) {
      return (
        <Stack gap='sm' alignItems='stretch'>
          {
            (CustomTabStore.hasOutstandingRequests || MembershipTypeStore.hasOutstandingRequests) &&
              <Spinner background={AppUI.site.accentColor} />
          }
          {
            isBlank(this.props.worksheetCustomTab) &&
              <>
                <Grid columns={[5,2,3,2]}>
                  <fieldset style={{display: 'flex'}}>
                    <legend>Name</legend>
                    <InPlaceText
                      id={this.customTab.id}
                      defaultValue={this.customTab.title}
                      model='custom_tabs'
                      field='title'
                    />
                    {
                      isBlank(this.customTab.title) &&
                      <InfoTooltip icon='warning'>Don't forget to add a name!</InfoTooltip>
                    }
                  </fieldset>
                  <fieldset style={{display: 'flex'}}>
                    <legend>Published</legend>
                    <InPlaceCheckbox
                      value={this.customTab.published}
                      id={this.customTab.id}
                      field='published'
                      model='custom_tabs'
                      afterChange={this.afterUpdate}
                    />
                    {
                      !this.customTab.published &&
                      <InfoTooltip icon='warning'>The custom tab isn't published</InfoTooltip>
                    }
                  </fieldset>
                  <fieldset style={{display: 'flex', alignItems: 'center'}}>
                    <legend>Icon</legend>
                    <IconInPlaceSelect
                      includeBlank={false}
                      id={this.customTab.id}
                      value={this.customTab.icon}
                      model='custom_tabs'
                      field='icon'
                      options={[
                        ['question-circle', 'Question'],
                        ['video-camera', 'Video'],
                        ['link', 'Link'],
                        ['download', 'Download'],
                        ['play-circle', 'Play Button'],
                        ['puzzle-piece', 'Puzzle'],
                        ['lightbulb-o', 'Lightbulb'],
                        ['rocket', 'Rocket'],
                        ['globe', 'Globe'],
                      ]}
                      afterChange={this.afterUpdate}
                    />
                  </fieldset>
                  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}>
                    <DeleteButton
                      onConfirm={this.onDelete}
                      message='Delete this Custom Tab?'
                      className='fa-block right'
                    >
                      <Button>Delete</Button>
                    </DeleteButton>
                  </div>
                </Grid>
                {
                  !this.customTab.isVisible &&
                    <Alert>You haven't selected any membership types or selected to show to non members. No members will be able to see this custom tab.</Alert>
                }
                <Grid columns={2}>
                  <fieldset>
                    <legend>Membership Types</legend>
                    {
                      this.filteredMembershipTypes.map(item => {
                        return (
                          <div
                            key={item.id}
                          >
                            <InPlaceCheckbox
                              value={this.customTab.membershipTypeIds?.includes(item.id)}
                              model='membership_type_custom_tabs'
                              onChange={() => this.onUpdateMembershipType(item.id)}
                            >
                              {item.name}
                            </InPlaceCheckbox>
                          </div>
                        );
                      })
                    }
                  </fieldset>
                  <fieldset style={{width: '100%'}}>
                    <legend>Other Filters</legend>
                    <div>
                      <InPlaceCheckbox
                        id={this.customTab.id}
                        value={this.customTab?.showToNonMembers}
                        field='show_to_non_members'
                        model='custom_tabs'
                        afterChange={this.afterUpdate}
                      >
                        Show to non members
                      </InPlaceCheckbox>
                    </div>
                    <div>
                      <InPlaceCheckbox
                        id={this.customTab.id}
                        value={this.customTab?.blockIfNotMigrated}
                        field='block_if_not_migrated'
                        model='custom_tabs'
                        afterChange={this.afterUpdate}
                      >
                        Block Content if User Needs to Migrate
                      </InPlaceCheckbox>
                    </div>

                  </fieldset>
                </Grid>
                <BackEndExplainer>You can fill in the following fields, and you will also be able to replace these when adding the custom tab to a post.</BackEndExplainer>
              </>
          }
          {
            isPresent(this.props.worksheetCustomTab) &&
              <>
                {
                  !this.customTab.published &&
                    <Alert>This custom tab is not published.</Alert>
                }
                <Grid columns={[3,1]}>
                  <BackEndExplainer>
                    <ul>
                    {
                      (this.customTab?.membershipTypeIds && this.customTab.membershipTypeIds.length > 0 || this.customTab.showToNonMembers) &&
                        <li>{`Visibility: ${this.customTab.formattedMembershipTypeNames}.`}</li>
                    }
                    {
                      (!this.customTab.membershipTypeIds || this.customTab.membershipTypeIds.length === 0) && !this.customTab.showToNonMembers &&
                        <li>Visibility: No membership status have been selected.</li>
                    }
                    {
                      this.customTab.blockIfNotMigrated &&
                        <li>The content will be blocked for users that need to migrate their payment method.</li>
                    }
                    </ul>
                  </BackEndExplainer>
                  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}>
                  {
                    AppUI.groupId &&
                      <Link to={{name: 'creator.custom_tabs.edit', params: {groupId: AppUI.groupId, customTabId: this.customTab.id}}}>
                        <GrayButton>Edit Custom Tab Template</GrayButton>
                      </Link>
                  }
                 </div>
              </Grid>
            </>
          }
            <InPlaceText
              legend='Text'
              wrapperClassName='word-info'
              richText
              defaultValue={this.customTabOrWorksheetCustomTab('content')}
              placeholder={this.customTab.content}
              model={this.model}
              field='content'
              inputType='textarea'
              id={this.customTabOrWorksheetCustomTab('id')}
            />
            <fieldset>
              <legend>Image</legend>
              <Text fontSize='15px'>This image will be displayed with full width under the text.</Text>
              <InPlaceImageUpload
                image={isPresent(this.props.worksheetCustomTab?.image) ? this.props.worksheetCustomTab.image : this.customTab.image}
                model='images'
                onChange={this.afterUpdate}
                querystring={{
                  imageable_id: this.customTabOrWorksheetCustomTab('id'),
                  imageable_type: isPresent(this.props.worksheetCustomTab) ? 'WorksheetCustomTab' : 'CustomTab',
                }}
                hideDelete={isPresent(this.props.worksheetCustomTab) && isBlank(this.props.worksheetCustomTab?.image)}
              />
              <InPlaceText
                show
                id={this.customTabOrWorksheetCustomTab('id')}
                defaultValue={this.customTabOrWorksheetCustomTab('imageLinkUrl')}
                placeholder={this.customTab.imageLinkUrl}
                model={this.model}
                field='image_link_url'
                label='Image link url'
              />
            </fieldset>
            <fieldset>
              <legend>Youtube Video</legend>
              <Text fontSize='15px'>This video will be embedded with full width under the text and image (if there are any).</Text>
              <InPlaceText
                show
                id={this.customTabOrWorksheetCustomTab('id')}
                defaultValue={this.customTabOrWorksheetCustomTab('youtubeUrl')}
                model={this.model}
                field='youtube_url'
                placeholder={this.customTab.youtubeUrl}
              />
            </fieldset>
            <fieldset>
              <legend>Button Link</legend>
              <Text fontSize='15px'>This button will be displayed at the bottom of the tab.</Text>
              <InPlaceText
                show
                id={this.customTabOrWorksheetCustomTab('id')}
                defaultValue={this.customTabOrWorksheetCustomTab('buttonUrl')}
                model={this.model}
                field='button_url'
                label='Button URL'
                placeholder={this.customTab.buttonUrl}
              />
              <InPlaceText
                show
                id={this.customTabOrWorksheetCustomTab('id')}
                defaultValue={this.customTabOrWorksheetCustomTab('buttonText')}
                model={this.model}
                field='button_text'
                label='Button text'
                placeholder={this.customTab.buttonText}
              />
            </fieldset>
        </Stack>
      );
    }
  }
}

export default CustomTabEdit;
